import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { employerAPI, paymentsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import VerificationRequiredModal from '../../components/common/VerificationRequiredModal';
import ScheduleReviewModal from '../../components/employer/ScheduleReviewModal';
import { VettScoreBar } from '../../components/common/VettScoreBar';
import { toast } from 'sonner';
import { 
  User, MapPin, Briefcase, Star, Bookmark, Send, Loader2, Lock, 
  CheckCircle, ChevronLeft, Download, Clock, Building2, Users,
  TrendingUp, Award, MessageSquare, Calendar, Shield, FileText,
  Target, Zap, Heart, AlertTriangle, Crown, ArrowRight, CalendarPlus, Plus
} from 'lucide-react';
import { CompanyTierBadge } from '../../components/common/CompanyTierCard';

const TalentProfile = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { emailVerified } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [reachOutOpen, setReachOutOpen] = useState(false);
  const [reachOutMessage, setReachOutMessage] = useState('');
  const [sendingReachOut, setSendingReachOut] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [addingToPipeline, setAddingToPipeline] = useState(false);
  const [inPipeline, setInPipeline] = useState(false);
  const [accessStatus, setAccessStatus] = useState(null);
  const [requestingAccess, setRequestingAccess] = useState(false);

  useEffect(() => {
    fetchProfile();
    checkPipelineStatus();
    checkAccessStatus();
  }, [employeeId]);

  const fetchProfile = async () => {
    try {
      const res = await employerAPI.getTalentProfile(employeeId);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load profile');
      navigate('/employer/search');
    } finally {
      setLoading(false);
    }
  };

  const checkPipelineStatus = async () => {
    try {
      const res = await employerAPI.getPipelineCandidates();
      const isInPipeline = res.data.candidates.some(c => c.employee_id === employeeId);
      setInPipeline(isInPipeline);
    } catch (error) {
      // Silently fail
    }
  };

  const checkAccessStatus = async () => {
    try {
      const res = await employerAPI.checkAccessStatus(employeeId);
      setAccessStatus(res.data);
    } catch (error) {
      // Silently fail
    }
  };

  const handleRequestAccess = async () => {
    setRequestingAccess(true);
    try {
      const res = await employerAPI.requestFullAccess(employeeId);
      setAccessStatus(prev => ({ ...prev, request_status: res.data.status }));
      if (res.data.status === 'approved') {
        toast.success('You already have full access to this profile');
        fetchProfile();
      } else {
        toast.success('Access request sent! The candidate will be notified.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request access');
    } finally {
      setRequestingAccess(false);
    }
  };

  const handleAddToPipeline = async () => {
    setAddingToPipeline(true);
    try {
      await employerAPI.addToPipeline({ employee_id: employeeId, stage_id: 'spotted' });
      setInPipeline(true);
      toast.success('Added to pipeline!');
    } catch (error) {
      if (error.response?.data?.detail?.includes('already')) {
        setInPipeline(true);
        toast.info('Already in pipeline');
      } else {
        toast.error('Failed to add to pipeline');
      }
    } finally {
      setAddingToPipeline(false);
    }
  };

  const handleSaveCandidate = async () => {
    try {
      const res = await employerAPI.saveCandidate(employeeId);
      toast.success(res.data.saved ? 'Candidate saved' : 'Candidate unsaved');
    } catch (error) {
      toast.error('Failed to save candidate');
    }
  };

  const handleReachOutClick = () => {
    // Check email verification first
    if (!emailVerified) {
      setShowVerificationModal(true);
      return;
    }
    setReachOutOpen(true);
  };

  const handleSendReachOut = async () => {
    if (!reachOutMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setSendingReachOut(true);
    try {
      await employerAPI.sendReachOut(employeeId, reachOutMessage);
      toast.success('Reach-out sent!');
      setReachOutOpen(false);
      setReachOutMessage('');
    } catch (error) {
      if (error.response?.data?.detail?.includes('limit')) {
        toast.error('Monthly reach-out limit reached. Upgrade for more.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send reach-out');
      }
    } finally {
      setSendingReachOut(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data?.can_export_pdf) {
      setUpgradeReason('pdf');
      setUpgradeModalOpen(true);
      return;
    }
    setExportingPdf(true);
    try {
      const res = await employerAPI.exportTalentPdf(employeeId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vett-ledger-${data.profile?.first_name || 'candidate'}-${data.profile?.last_name || ''}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const res = await paymentsAPI.createCheckout(planId, window.location.origin);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  const showUpgradePrompt = (reason) => {
    setUpgradeReason(reason);
    setUpgradeModalOpen(true);
  };

  // Helper to render locked section
  const LockedSection = ({ title, description, requiredPlan = 'starter' }) => (
    <Card className="bg-[#F5F8FF]/30 border border-[#E8EDF5] rounded-xl p-6 relative overflow-hidden">
      <div className="absolute inset-0 backdrop-blur-sm bg-[#0F0F1A]/60 z-10 flex items-center justify-center">
        <div className="text-center p-6">
          <Lock className="w-8 h-8 text-[#1B5AFF] mx-auto mb-3" />
          <p className="text-[#0A0F1E] font-medium mb-2">Upgrade to {requiredPlan === 'growth' ? 'Growth' : 'Starter'} to unlock</p>
          <p className="text-[#5B6472] text-sm mb-4">{description}</p>
          <Button 
            onClick={() => showUpgradePrompt(title)} 
            className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
          >
            Upgrade Now
          </Button>
        </div>
      </div>
      <h3 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
        <Lock className="w-5 h-5 text-[#5B6472]" />
        {title}
      </h3>
      <div className="h-24 bg-[#FFFFFF]/50 rounded-lg animate-pulse" />
    </Card>
  );

  // Render star rating
  const StarRating = ({ rating, label }) => (
    <div className="flex items-center justify-between">
      <span className="text-[#0A0F1E] text-sm">{label}</span>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`}
          />
        ))}
      </div>
    </div>
  );

  // Rehire badge styling
  const getRehireBadge = (status) => {
    const badges = {
      'yes_without_reservation': { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Would Rehire - Without Reservation', shortLabel: 'Would Rehire', icon: CheckCircle },
      'yes_with_right_role': { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Would Rehire - With Right Role', shortLabel: 'Conditional Rehire', icon: Target },
      'no': { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Would Not Rehire', shortLabel: 'No Rehire', icon: AlertTriangle },
    };
    const badge = badges[status] || badges['yes_without_reservation'];
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
        <span className="hidden sm:inline">{badge.label}</span>
        <span className="sm:hidden">{badge.shortLabel}</span>
      </span>
    );
  };

  // Collaboration style mapping
  const collaborationStyles = {
    'team_player': 'Team Player',
    'independent': 'Independent Worker',
    'leader': 'Natural Leader',
    'collaborative_leader': 'Collaborative Leader',
    'mentoring': 'Mentor/Coach',
    'adaptable': 'Highly Adaptable'
  };

  // Departure reasons mapping
  const departureReasons = {
    'career_growth': 'Career Growth',
    'relocation': 'Relocation',
    'personal': 'Personal Reasons',
    'company_restructure': 'Company Restructure',
    'contract_end': 'Contract End',
    'mutual_decision': 'Mutual Decision',
    'layoff': 'Layoff',
    'resignation': 'Voluntary Resignation',
    'other': 'Other'
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data) return null;

  const { profile, records, years_experience, verified_records_count, subscription_tier, can_export_pdf } = data;
  const isFree = subscription_tier === 'free';
  const isStarter = subscription_tier === 'starter';
  const isGrowth = subscription_tier === 'growth';

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="talent-profile-page">
        {/* Back Button & Actions */}
        <div className="flex items-center justify-between animate-fade-in">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#5B6472] hover:text-[#0A0F1E]"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleSaveCandidate}
              className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
              data-testid="save-candidate-btn"
            >
              <Bookmark className="mr-2 h-4 w-4" />
              Save
            </Button>
            <Button
              variant="outline"
              onClick={handleAddToPipeline}
              disabled={addingToPipeline || inPipeline}
              className={`border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl ${inPipeline ? 'border-green-500/50 text-green-400' : ''}`}
              data-testid="add-to-pipeline-btn"
            >
              {addingToPipeline ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : inPipeline ? (
                <CheckCircle className="mr-2 h-4 w-4" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {inPipeline ? 'In Pipeline' : 'Add to Pipeline'}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportPdf}
              disabled={exportingPdf}
              className={`border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl ${!can_export_pdf ? 'opacity-60' : ''}`}
              data-testid="export-pdf-btn"
            >
              {exportingPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {can_export_pdf ? 'Export PDF' : (
                <>
                  <Lock className="mr-1 h-3 w-3" />
                  Export PDF
                </>
              )}
            </Button>
            <Button
              onClick={handleReachOutClick}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] btn-glow rounded-xl"
              data-testid="reach-out-btn"
            >
              <Send className="mr-2 h-4 w-4" />
              Reach Out
            </Button>
          </div>
        </div>

        {/* Selective Visibility Access Banner */}
        {accessStatus?.visibility === 'selective' && !accessStatus?.has_full_access && (
          <Card className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 animate-fade-in">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-blue-400 font-medium">Limited Profile View</p>
                  <p className="text-[#5B6472] text-sm">
                    {accessStatus?.request_status === 'pending' 
                      ? 'Your access request is pending approval from the candidate.'
                      : 'This candidate has selective visibility. Request access to view full records.'}
                  </p>
                </div>
              </div>
              {accessStatus?.request_status !== 'pending' && (
                <Button
                  onClick={handleRequestAccess}
                  disabled={requestingAccess}
                  className="bg-blue-500 hover:bg-blue-600 text-[#0A0F1E] rounded-xl whitespace-nowrap"
                  data-testid="request-access-btn"
                >
                  {requestingAccess ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  Request Full Access
                </Button>
              )}
              {accessStatus?.request_status === 'pending' && (
                <Badge className="bg-amber-500/20 text-amber-400 border-0">
                  <Clock className="w-3 h-3 mr-1" />
                  Request Pending
                </Badge>
              )}
            </div>
          </Card>
        )}

        {/* Report Header */}
        <Card className="bg-gradient-to-br from-[#FFFFFF] to-[#FFFFFF] border border-[#E8EDF5] rounded-2xl p-8 animate-fade-in">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-[#1B5AFF]" />
            <span className="text-[#1B5AFF] font-semibold text-sm tracking-wider uppercase">VETT Ledger Report</span>
          </div>
          <div className="flex flex-col md:flex-row items-start gap-6">
            {profile?.photo_url ? (
              <img 
                src={profile.photo_url} 
                alt="" 
                className="w-28 h-28 rounded-2xl object-cover border-2 border-[#1B5AFF]/50 shadow-lg shadow-[#1B5AFF]/20" 
              />
            ) : (
              <div className="w-28 h-28 rounded-2xl bg-[#0F0F1A] border-2 border-[#E8EDF5] flex items-center justify-center">
                <User className="w-12 h-12 text-[#5B6472]" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-[#0A0F1E]">
                  {profile?.first_name || profile?.last_name 
                    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                    : 'Anonymous Candidate'}
                </h1>
                {profile?.open_to_opportunities && profile?.open_to_opportunities !== 'not_looking' && profile?.open_to_opportunities !== 'hidden' && (
                  <Badge className={`${
                    profile.open_to_opportunities === 'open' 
                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                      : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                  } border`}>
                    {profile.open_to_opportunities === 'open' ? 'Open to Opportunities' : 'Casually Looking'}
                  </Badge>
                )}
              </div>
              <p className="text-xl text-[#1B5AFF] font-medium mb-3">{profile?.job_title || 'Professional'}</p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[#5B6472]">
                {profile?.location && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    {profile.location}
                  </span>
                )}
                {profile?.industry && (
                  <span className="flex items-center gap-1.5">
                    <Briefcase className="w-4 h-4" />
                    {profile.industry}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {years_experience} years experience
                </span>
                <span className="flex items-center gap-1.5 text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  {verified_records_count} verified record{verified_records_count !== 1 ? 's' : ''}
                </span>
              </div>
              
              {/* VETT Score Bar */}
              {data.vett_score > 0 && (
                <div className="mt-4 max-w-sm">
                  <VettScoreBar score={data.vett_score} label="VETT" size="md" />
                </div>
              )}
            </div>
          </div>
          
          {/* Bio */}
          {profile?.bio && (
            <div className="mt-6 pt-6 border-t border-[#E8EDF5]">
              <p className="text-[#0A0F1E] leading-relaxed">{profile.bio}</p>
            </div>
          )}

          {/* Skills */}
          {profile?.skills?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#E8EDF5]">
              <h3 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3">Core Skills</h3>
              <div className="flex flex-wrap gap-2">
                {profile.skills.map((skill, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-lg bg-[#1B5AFF]/10 text-[#1B5AFF] text-sm font-medium border border-[#1B5AFF]/20">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Free Tier Paywall */}
        {isFree && (
          <Card className="bg-gradient-to-r from-[#1B5AFF]/10 to-[#FFFFFF] border border-[#1B5AFF]/30 rounded-xl p-6 animate-fade-in">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#1B5AFF]/20 flex items-center justify-center flex-shrink-0">
                <Crown className="w-6 h-6 text-[#1B5AFF]" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-[#0A0F1E] mb-1">Unlock Full Profile Access</h3>
                <p className="text-[#5B6472] text-sm mb-4">
                  Upgrade to see detailed work history, performance ratings, achievements, and employer insights.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => handleUpgrade('starter')}
                    variant="outline"
                    className="border-[#1B5AFF]/30 text-[#1B5AFF] hover:bg-[#1B5AFF]/10 rounded-xl"
                  >
                    Starter $299/mo
                  </Button>
                  <Button
                    onClick={() => handleUpgrade('growth')}
                    className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl btn-glow"
                  >
                    Growth $799/mo <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Verified Work Records */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-[#0A0F1E] flex items-center gap-2 animate-fade-in">
            <FileText className="w-5 h-5 text-[#1B5AFF]" />
            Verified Work History
          </h2>

          {records?.length > 0 ? (
            records.map((record, recordIndex) => (
              <Card 
                key={record.id} 
                className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-2xl overflow-hidden animate-fade-in"
                style={{ animationDelay: `${recordIndex * 0.1}s` }}
                data-testid={`record-card-${record.id}`}
              >
                {/* Record Header */}
                <div className="bg-gradient-to-r from-[#F5F8FF] to-[#FFFFFF] p-6 border-b border-[#E8EDF5]">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      {record.employer_logo ? (
                        <img src={record.employer_logo} alt="" className="w-14 h-14 rounded-xl object-cover border border-[#E8EDF5]" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-[#0F0F1A] border border-[#E8EDF5] flex items-center justify-center">
                          <Building2 className="w-7 h-7 text-[#5B6472]" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-bold text-[#0A0F1E]">{record.job_title}</h3>
                        <div className="flex items-center gap-3 flex-wrap">
                          <p className="text-[#1B5AFF] font-medium">{record.employer_name}</p>
                          {record.employer_tier && (
                            <CompanyTierBadge 
                              tier={record.employer_tier.tier} 
                              tier_name={record.employer_tier.tier_name}
                              size="sm"
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[#5B6472] text-sm">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {record.start_date} - {record.currently_employed ? 'Present' : record.end_date}
                          </span>
                          {record.department && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              {record.department}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Rehire badge - only for past employees */}
                    {!record.locked && !record.currently_employed && record.rehire_eligibility && (
                      <div>{getRehireBadge(record.rehire_eligibility)}</div>
                    )}
                    {/* Currently employed badge */}
                    {record.currently_employed && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Currently Employed</Badge>
                    )}
                  </div>
                  
                  {/* Employment Type & Reporting Line */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {record.employment_type && (
                      <Badge variant="secondary" className="bg-[#0F0F1A] text-[#0A0F1E] border border-[#E8EDF5]">
                        {record.employment_type}
                      </Badge>
                    )}
                    {record.reporting_to && (
                      <Badge variant="secondary" className="bg-[#0F0F1A] text-[#0A0F1E] border border-[#E8EDF5]">
                        Reports to: {record.reporting_to}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Record Content - Gated by subscription */}
                {record.locked ? (
                  <div className="p-6">
                    <LockedSection 
                      title="Full Record Details" 
                      description="See daily responsibilities, achievements, performance ratings, and employer insights."
                      requiredPlan="starter"
                    />
                  </div>
                ) : (
                  <div className="p-6 space-y-6">
                    {/* Daily Responsibilities */}
                    {record.daily_responsibilities?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Briefcase className="w-4 h-4" />
                          Day-to-Day Responsibilities
                        </h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {record.daily_responsibilities.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-[#0A0F1E]">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#1B5AFF] mt-2 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Key Achievements */}
                    {record.key_achievements?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Award className="w-4 h-4" />
                          Key Achievements
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {record.key_achievements.map((achievement, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-[#0A0F1E]">{achievement}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Skills from this role */}
                    {record.skills?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Skills Demonstrated
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {record.skills.map((skill, i) => (
                            <span key={i} className="px-3 py-1.5 rounded-lg bg-[#0F0F1A] text-[#0A0F1E] text-sm border border-[#E8EDF5]">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Strengths & Growth Areas */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {record.strengths && (
                        <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/20">
                          <h4 className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Strengths
                          </h4>
                          <p className="text-[#0A0F1E] text-sm">{record.strengths}</p>
                        </div>
                      )}
                      {record.growth_areas && (
                        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                          <h4 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            Growth Areas
                          </h4>
                          <p className="text-[#0A0F1E] text-sm">{record.growth_areas}</p>
                        </div>
                      )}
                    </div>

                    {/* Work Ethic Ratings */}
                    {(record.punctuality_rating || record.deadline_rating || record.initiative_rating || record.adaptability_rating) && (
                      <div>
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Star className="w-4 h-4" />
                          Work Ethic & Reliability
                        </h4>
                        <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                          {record.punctuality_rating && <StarRating rating={record.punctuality_rating} label="Punctuality" />}
                          {record.deadline_rating && <StarRating rating={record.deadline_rating} label="Meeting Deadlines" />}
                          {record.initiative_rating && <StarRating rating={record.initiative_rating} label="Initiative" />}
                          {record.adaptability_rating && <StarRating rating={record.adaptability_rating} label="Adaptability" />}
                        </div>
                        {record.work_ethic_note && (
                          <p className="mt-3 text-[#5B6472] text-sm italic">"{record.work_ethic_note}"</p>
                        )}
                      </div>
                    )}

                    {/* Collaboration Style */}
                    {record.collaboration_style && (
                      <div>
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Collaboration Style
                        </h4>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-[#1B5AFF]/10 text-[#1B5AFF] border border-[#1B5AFF]/20 text-sm px-4 py-1.5">
                            {collaborationStyles[record.collaboration_style] || record.collaboration_style}
                          </Badge>
                          {record.collaboration_note && (
                            <span className="text-[#5B6472] text-sm italic">"{record.collaboration_note}"</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Peer & Team Perception - Growth only */}
                    {record.peer_team_perception ? (
                      <div>
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Heart className="w-4 h-4" />
                          Peer & Team Perception
                        </h4>
                        <div className="p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                          <p className="text-[#0A0F1E]">{record.peer_team_perception}</p>
                        </div>
                      </div>
                    ) : record.locked_fields?.includes('peer_team_perception') && (
                      <LockedSection 
                        title="Peer & Team Perception" 
                        description="Detailed feedback on how this candidate was perceived by their peers and team members."
                        requiredPlan="growth"
                      />
                    )}

                    {/* Reason for Departure - Only show for past employees */}
                    {!record.currently_employed && record.reason_for_departure && (
                      <div>
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Reason for Departure
                        </h4>
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary" className="bg-[#0F0F1A] text-[#0A0F1E] border border-[#E8EDF5]">
                            {departureReasons[record.reason_for_departure] || record.reason_for_departure}
                          </Badge>
                          {record.departure_note && (
                            <span className="text-[#5B6472] text-sm italic">"{record.departure_note}"</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Rehire Note - Only show for past employees */}
                    {!record.currently_employed && record.rehire_note && (
                      <div className="p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-2">Rehire Context</h4>
                        <p className="text-[#0A0F1E] text-sm">{record.rehire_note}</p>
                      </div>
                    )}

                    {/* Currently Employed Badge */}
                    {record.currently_employed && (
                      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                        <div className="flex items-center gap-2">
                          <Check className="w-5 h-5 text-green-400" />
                          <span className="text-green-400 font-medium">Currently Employed</span>
                        </div>
                      </div>
                    )}

                    {/* Closing Statement - Growth only */}
                    {record.closing_statement ? (
                      <div className="border-t border-[#E8EDF5] pt-6">
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Closing Statement from Employer
                        </h4>
                        <div className="p-5 rounded-xl bg-gradient-to-br from-[#1B5AFF]/5 to-transparent border border-[#1B5AFF]/20">
                          <p className="text-[#0A0F1E] leading-relaxed italic">"{record.closing_statement}"</p>
                          <div className="mt-4 flex items-center gap-2 text-[#5B6472] text-sm">
                            <Building2 className="w-4 h-4" />
                            <span>{record.employer_name}</span>
                          </div>
                        </div>
                      </div>
                    ) : record.locked_fields?.includes('closing_statement') && (
                      <LockedSection 
                        title="Closing Statement" 
                        description="A personal closing statement from the employer summarizing their experience with this candidate."
                        requiredPlan="growth"
                      />
                    )}

                    {/* Public Note (Legacy) */}
                    {record.public_note && (
                      <div className="p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                        <h4 className="text-sm font-semibold text-[#5B6472] uppercase tracking-wider mb-2">Additional Note</h4>
                        <p className="text-[#0A0F1E] text-sm italic">"{record.public_note}"</p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ))
          ) : (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
              <Briefcase className="w-12 h-12 text-[#5B6472] mx-auto mb-3" />
              <p className="text-[#5B6472]">No verified work records yet</p>
            </Card>
          )}
        </div>

        {/* Reach Out Dialog */}
        <Dialog open={reachOutOpen} onOpenChange={setReachOutOpen}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
            <DialogHeader>
              <DialogTitle>Send a Reach-Out</DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                Introduce yourself and explain why you're reaching out to this candidate.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={reachOutMessage}
              onChange={(e) => setReachOutMessage(e.target.value.slice(0, 500))}
              placeholder="Hi, I came across your VETT profile and was impressed by..."
              className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[150px]"
              data-testid="reach-out-textarea"
            />
            <div className="flex justify-end text-[#5B6472] text-xs">
              {reachOutMessage.length}/500
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReachOutOpen(false)} className="border-[#E8EDF5] text-[#0A0F1E]">
                Cancel
              </Button>
              <Button 
                onClick={handleSendReachOut} 
                disabled={sendingReachOut}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
                data-testid="send-reach-out-btn"
              >
                {sendingReachOut ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send Message
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upgrade Modal */}
        <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#1B5AFF]" />
                Upgrade to Unlock
              </DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                {upgradeReason === 'pdf' 
                  ? 'PDF export is available on the Growth plan. Download detailed candidate reports to share with your team.'
                  : 'Get full access to candidate insights, performance data, and employer feedback.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-xl border border-[#E8EDF5] hover:border-[#1B5AFF]/50 transition-colors cursor-pointer" onClick={() => handleUpgrade('starter')}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-[#0A0F1E]">Starter Plan</h4>
                  <span className="text-[#1B5AFF] font-bold">$299/mo</span>
                </div>
                <p className="text-[#5B6472] text-sm">Full record access, work history, ratings, and achievements.</p>
              </div>
              <div className="p-4 rounded-xl border-2 border-[#1B5AFF] bg-[#1B5AFF]/5 cursor-pointer" onClick={() => handleUpgrade('growth')}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-[#0A0F1E]">Growth Plan</h4>
                    <Badge className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] text-xs">Recommended</Badge>
                  </div>
                  <span className="text-[#1B5AFF] font-bold">$799/mo</span>
                </div>
                <p className="text-[#5B6472] text-sm">Everything in Starter plus closing statements, peer perception, and PDF export.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUpgradeModalOpen(false)} className="border-[#E8EDF5] text-[#0A0F1E]">
                Maybe Later
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Verification Required Modal */}
        <VerificationRequiredModal
          open={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          featureName="sending reach-outs"
        />
        
        {/* Schedule Review Modal - Pre-filled with employee */}
        <ScheduleReviewModal
          open={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onSuccess={() => {
            setShowScheduleModal(false);
            toast.success('Review scheduled for this employee!');
          }}
          prefilledEmployee={data?.profile ? {
            id: employeeId,
            first_name: data.profile.first_name,
            last_name: data.profile.last_name,
            photo_url: data.profile.photo_url,
            job_title: data.profile.job_title,
          } : null}
        />
      </div>
    </DashboardLayout>
  );
};

export default TalentProfile;
